import React, { useState, useEffect, useRef } from 'react';
import { useStateValue } from './StateProvider.js';
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";

import { Asset } from './Utils/asset.js';
import { elementTypes, Element, ElementPreview } from './Utils/structure.js';
import { getPermission } from './Utils/permissions.js';
import { assetsremovalprotection } from './Utils/removeprotection.js';
import Select from './Components/select.js';
import { renderWidget } from './Utils/widgets.js'
import { loadAssets, loadStructure } from './Utils/dataloader.js'

import axios from 'axios';
import configData from './Config';

import Title from './Title.js';

import './Summary.css';

function Structure({ state, setState }) {
    const [{ project }, dispatch] = useStateValue();

    const [assets, setAssets] = useState(true);
    const [invisible, setInvisible] = useState(false);

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState('');

    const canvasRef = useRef();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        loadAssets(project, state, dispatch);
        loadStructure(project, state, dispatch);
    }, []);

    const handleCloseModal = () => {
        setState(state => ({ ...state, modalopen: false, modalcontent: null, modaltype: null, modaltitle: null }));
    }

    const screenshot = () => { // not working
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const screenshotUrl = canvas.toDataURL('image/png');

            const link = document.createElement('a');
            link.href = screenshotUrl;
            link.download = '3d_structure.png';
            link.click();
        }
    };

    const FirstPersonControls = () => {
        const [movement, setMovement] = useState({ forward: false, backward: false });
        const [speed, setSpeed] = useState(0.1);

        const camera = useThree((state) => state.camera);
        const { forward, backward } = movement;

        useEffect(() => {
            const handleKeyDown = (e) => {
                if (e.key === 'w' || e.key === 'ArrowUp') {
                    setMovement((prev) => ({ ...prev, forward: true }));
                }
                if (e.key === 's' || e.key === 'ArrowDown') {
                    setMovement((prev) => ({ ...prev, backward: true }));
                }
            };

            const handleKeyUp = (e) => {
                if (e.key === 'w' || e.key === 'ArrowUp') {
                    setMovement((prev) => ({ ...prev, forward: false }));
                }
                if (e.key === 's' || e.key === 'ArrowDown') {
                    setMovement((prev) => ({ ...prev, backward: false }));
                }
            };

            window.addEventListener('keydown', handleKeyDown);
            window.addEventListener('keyup', handleKeyUp);

            return () => {
                window.removeEventListener('keydown', handleKeyDown);
                window.removeEventListener('keyup', handleKeyUp);
            };
        }, []);

        useFrame(() => {
            if (forward) {
                camera.position.z -= speed;
            }
            if (backward) {
                camera.position.z += speed;
            }
        });

        return null;
    };

    function RemoveElement({ element }) {
        const [id, setId] = useState('');

        const [result, setResult] = useState(null);
        const [loading, setLoading] = useState(false);

        useEffect(() => {
            setId(element.id)
        }, [element]);

        const removeElement = () => {
            console.log('Trying to remove element');

            setResult('');

            const hasPermission = getPermission(
                project.projectuserid,
                project.users,
                state.user.userid,
                'Structure',
                'remove',
                project.archived
            );
            if (hasPermission.code === 0) {
                setResult(hasPermission.data);
                return;
            }

            const assetsData = assetsremovalprotection(project.assets, id, 'structure');
            if (assetsData.code) {
                setResult(assetsData.message);
                return
            }

            setLoading(true);

            const data = {
                projectuserid: project.projectuserid,
                projectid: project.projectid,
                id: id
            }

            axios.post(configData.CONTROLLERURL + configData.REMOVEELEMENT, data, {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json;charset=UTF-8",
                    "userid": state.user.userid,
                    "usertoken": state.user.usertoken
                }
            }).then((res) => {
                console.log('Remove element data received')
                console.log(res.data)
                if (res.data instanceof Object) {
                    if (res.data.code === 1) {
                        handleCloseModal();
                    }
                    else {
                        setResult(res.data.data)
                    }
                }
                else {
                    setResult('Error')
                }
            }).catch((err) => {
                setResult(err.response?.data?.message || err.message || 'Network error');
            }).finally(() => {
                setLoading(false);
            });
        }

        return (
            <div className='modal_body'>
                <div className='modal_actions'>
                    {result && <div className='modal_result'>{result}</div>}
                    <div className='modal_buttons'>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={() => removeElement()} disabled={loading}>{loading ? 'Loading...' : 'Remove'}</button>
                        </div>
                        <div className='modal_buttoncontainer'>
                            <button className="modal_button" onClick={handleCloseModal}>Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function getAbsolutePosition(element, structure) {
        let px = parseFloat(element.px) || 0;
        let py = parseFloat(element.py) || 0;
        let pz = parseFloat(element.pz) || 0;

        let current = element;
        while (current.parentid) {
            const parent = structure.find(e => e.id === current.parentid);
            if (!parent) break;

            px += parseFloat(parent.px) || 0;
            py += parseFloat(parent.py) || 0;
            pz += parseFloat(parent.pz) || 0;

            current = parent;
        }

        return { px, py, pz };
    }

    return (
        <div className='structure'>
            <Title text='Summary' />

            <div className='buttonscontainer'>
                <div className='buttonsection'>
                    <div className='button'>
                        <label>
                            <input
                                type="checkbox"
                                checked={assets}
                                onChange={() => setAssets(!assets)}
                            />
                            Assets
                        </label>
                    </div>
                </div>
                <div className='button'>
                    <label>
                        <input
                            type="checkbox"
                            checked={invisible}
                            onChange={() => setInvisible(!invisible)}
                        />
                        Invisible
                    </label>
                </div>
            </div>

            <div className='summary_widgets'>
                {
                    project.widgets
                        .sort((a, b) => a.orderid - b.orderid)
                        .map((widget, index) => {
                            let timestamp = project.telemetry.find(point => point.assetid == widget.assetid)?.ts
                            let reading = project.telemetry.find(point => point.assetid == widget.assetid)?.reading
                            return renderWidget(widget.type, widget.name, widget.properties, timestamp, reading)
                        })
                }
            </div>

            <div className='structure_canvas'>
                <Canvas ref={canvasRef} camera={{ position: [20, 20, 20], fov: 50 }}>
                    <ambientLight intensity={0.5} />
                    <FirstPersonControls />
                    <OrbitControls
                        enableZoom={true}
                        enablePan={true}
                        autoRotate={true}
                        autoRotateSpeed={0.3}
                    />
                    {
                        project.structure.map((element, index) => {
                            const relatedAssets = project.assets.filter(asset => asset.elementid === element.id);
                            const { px, py, pz } = getAbsolutePosition(element, project.structure);

                            return (
                                <React.Fragment key={'structure' + index}>
                                    <Element
                                        element={{
                                            ...element,
                                            px: px,
                                            py: py,
                                            pz: pz
                                        }}
                                        wireframe={invisible}
                                    />
                                    {
                                        assets &&
                                        relatedAssets.map((asset, assetIndex) => {
                                            return (
                                                <Asset
                                                    key={'asset' + assetIndex}
                                                    asset={{
                                                        id: asset.id,
                                                        type: asset.type,
                                                        option: asset.option,
                                                        name: asset.name,
                                                        px: (parseFloat(px) || 0) + (parseFloat(asset.px) || 0),
                                                        py: (parseFloat(py) || 0) + (parseFloat(asset.py) || 0),
                                                        pz: (parseFloat(pz) || 0) + (parseFloat(asset.pz) || 0),
                                                        rotation: asset.rotation
                                                    }}
                                                    inspections={project.inspections}
                                                    tasks={project.tasks}
                                                />
                                            );
                                        })
                                    }
                                </React.Fragment>
                            );
                        })
                    }
                </Canvas>
            </div>
        </div >
    );
}

export default Structure;